import { Component, OnInit } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  OmnisearchEntity,
  OmnisearchQueryResponse,
} from '../../models/omnisearch.models';
import { OmnisearchService } from '../../services/omnisearch.service';

export type OmnisearchTab = 'all' | OmnisearchEntity;

export const OMNISEARCH_TABS: { label: string; value: OmnisearchTab }[] = [
  { label: 'All results', value: 'all' },
  { label: 'Projects', value: OmnisearchEntity.opportunities },
  { label: 'Clients', value: OmnisearchEntity.clients },
  { label: 'Experts', value: OmnisearchEntity.profiles },
  { label: 'Respondents', value: OmnisearchEntity.solicitationExperts },
];

@Component({
  selector: 'app-omnisearch',
  templateUrl: './omnisearch.component.html',
  styleUrls: ['./omnisearch.component.scss'],
  standalone: false,
})
export class OmnisearchComponent implements OnInit {
  private searchTerm$ = new Subject<string>();
  public isSearching$ = new BehaviorSubject<boolean>(false);
  results$: Observable<OmnisearchQueryResponse[]>;
  searchTerm: string = '';

  showDropdown: boolean = false;

  activeTab: OmnisearchTab = 'all';
  readonly allTabs = OMNISEARCH_TABS;

  constructor(
    private searchService: OmnisearchService,
    private router: Router
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(filter((e) => e instanceof ActivationEnd))
      .subscribe(() => {
        this.clearSearch();
      });

    this.results$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => {
        this.activeTab = 'all';
        this.isSearching$.next(true);
      }),
      switchMap((value) =>
        this.searchService.search(value).pipe(
          catchError(() => of([])),
          finalize(() => this.isSearching$.next(false))
        )
      )
    );
  }

  submitSearch(searchTerm: string) {
    if (searchTerm.trim().length >= 3) {
      this.showDropdown = true;
      this.searchTerm$.next(searchTerm);
    } else {
      this.showDropdown = false;
    }
  }

  clearSearch() {
    this.showDropdown = false;
  }

  onFocus() {
    if (this.searchTerm.trim().length >= 3) {
      this.showDropdown = true;
    }
  }

  selectTab(tab: OmnisearchTab) {
    this.activeTab = tab;
  }

  filterResults(results: OmnisearchQueryResponse[]): OmnisearchQueryResponse[] {
    if (this.activeTab === 'all') return results;
    return results.filter((r) => r.entity === this.activeTab);
  }

  getTabCount(results: OmnisearchQueryResponse[], tab: OmnisearchTab): number {
    if (tab === 'all') return results.length;
    return results.filter((r) => r.entity === tab).length;
  }

  hasEntity(
    results: OmnisearchQueryResponse[],
    entity: OmnisearchTab
  ): boolean {
    if (entity === 'all') return true;
    return results.some((r) => r.entity === entity);
  }
}
